import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { ChartData, SeriesVariant } from 'src/components/streametric/lineChart/types';
import { isDiscreteDataType } from 'src/features/trends/utils';

type ValueType = {
  [timestamp: string]: number;
};
type ReadingType = {
  displayName: string;
  rawInputName: string;
  rawUnit?: string;
  tagId: string;
  values: ValueType;
};

const BAND_VARIANTS: SeriesVariant[] = ['min', 'avg', 'max'];
const VARIANT_LABEL: Record<SeriesVariant, string> = { min: 'Min', avg: 'Avg', max: 'Max' };

const buildTransformedData = (values: ValueType[]): [number, number][] => {
  const removedEmptyObjects = values.filter((obj) => Object.keys(obj).length !== 0);
  const dataArray: { datetime: string; value: number }[] = [];

  for (const key in removedEmptyObjects) {
    if (Object.hasOwnProperty.call(removedEmptyObjects, key)) {
      const nestedData = removedEmptyObjects[key];
      for (const nestedKey in nestedData) {
        if (Object.hasOwnProperty.call(nestedData, nestedKey)) {
          dataArray.push({ datetime: nestedKey, value: nestedData[nestedKey] });
        }
      }
    }
  }

  return dataArray.map((entry) => {
    const utcDate = new Date(entry.datetime);
    const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60 * 1000);
    return [localDate.getTime(), entry.value];
  });
};

const buildSeriesName = (siteName: string, inputName: string, unit: string): string => {
  const displayUnit = unit && unit !== 'null' ? `(${unit})` : '';
  return siteName == null
    ? `${inputName} ${displayUnit}`.trim()
    : `${siteName} - ${inputName} ${displayUnit}`.trim();
};

export const getSeries = (
  readings: ReadingType[],
  selectedInputs: SelectedInputType[]
): ChartData[] => {
  if (readings.length === 0) return [];

  return selectedInputs.flatMap((x): ChartData[] => {
    const baseName = buildSeriesName(x.siteName, x.inputName, x.unit);
    const stepped = isDiscreteDataType(x.dataType);

    if (x.statistic === 'Min-Average-Max') {
      return BAND_VARIANTS.map((variant) => {
        const filteredTags = readings?.filter((item) => item.tagId === `${x.inputId}__${variant}`);
        const values = filteredTags?.map((item: any) => item.values);
        return {
          name: `${baseName} [${VARIANT_LABEL[variant]}]`,
          data: buildTransformedData(values),
          seriesVariant: variant,
          stepped,
        };
      });
    }

    const filteredTags = readings?.filter((item) => item.tagId === x.inputId);
    const values = filteredTags?.map((item: any) => item.values);
    return [{ name: baseName, data: buildTransformedData(values), stepped }];
  });
};
